/**
 * Role Detail Can-Deactivate Guard
 *
 * Prompts the user before navigating away from the Role Detail page when
 * there are unsaved changes. The guarded component must expose a `dirty()`
 * signal (or any zero-arg function returning a boolean) — the guard reads
 * that and surfaces a native `confirm()` dialog when truthy.
 *
 * RESPONSIBILITY: Hard navigation guard for the `/admin/roles/:id` route.
 * Pairs with the existing `beforeunload` handler in the shell, which guards
 * full page reloads / tab close.
 *
 * AI CONTEXT: We deliberately use `confirm()` (not a custom modal) here so
 * that the dialog appears synchronously *before* the route activates. This
 * keeps the guard side-effect free and avoids navigation race conditions.
 * Mirrors `space-detail-can-deactivate.guard.ts` — kept component-agnostic
 * via the structural `{ dirty?: () => boolean }` host contract.
 */
import { inject } from '@angular/core';
import { CanDeactivateFn } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';

export const roleDetailCanDeactivateGuard: CanDeactivateFn<{
  dirty?: () => boolean;
}> = (component) => {
  if (!component || typeof component.dirty !== 'function') return true;
  if (!component.dirty()) return true;
  const transloco = inject(TranslocoService);
  return window.confirm(transloco.translate('shared.confirms.discard_changes'));
};
